import React, { useEffect, useRef } from "react";
import { LinearProgress, Box, Typography, useTheme } from "@mui/material";

/**
 * Componente de barra de progresso para importação de arquivos
 *
 * @param {Object} props
 * @param {number} props.processedRows - Número de linhas processadas
 * @param {number} props.totalRows - Número total de linhas (0 se desconhecido)
 * @param {number} props.progressPercent - Percentual de progresso (0-100)
 * @param {string} props.status - Status do processamento (LOADING, LOADED, ERROR)
 * @param {Function} props.onComplete - Callback chamado após 1500ms quando status é LOADED
 */
export default function ProgressBar({
  processedRows,
  totalRows,
  progressPercent,
  status,
  onComplete,
}) {
  const theme = useTheme();
  const completionTimeoutRef = useRef(null);
  const hasCalledCompleteRef = useRef(false);

  // Calcula percentual se totalRows estiver disponível
  const calculatedPercent =
    totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : null;

  // Usa progressPercent se disponível, senão usa calculatedPercent, senão 0
  const displayPercent = progressPercent || calculatedPercent || 0;

  // Garante que o percentual seja 100% quando status é LOADED
  const finalPercent = status === "LOADED" ? 100 : displayPercent;

  // Determina se deve mostrar barra determinada ou indeterminada
  const isIndeterminate = totalRows === 0 && status === "LOADING";

  // Formata número com separador de milhares
  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toLocaleString("pt-BR");
  };

  // Mensagem de status
  const getStatusMessage = () => {
    if (status === "LOADED") {
      return "Processamento concluído!";
    }
    if (status === "ERROR") {
      return "Erro no processamento";
    }
    if (totalRows > 0) {
      return `Processando... ${displayPercent}% (${formatNumber(
        processedRows
      )} / ${formatNumber(totalRows)} linhas)`;
    }
    return `Processando... ${formatNumber(processedRows)} linhas processadas`;
  };

  // Efeito para gerenciar o delay de conclusão
  useEffect(() => {
    // Limpa timeout anterior se existir
    if (completionTimeoutRef.current) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }

    // Se status é LOADED e ainda não chamou o callback, aguarda 1500ms
    if (status === "LOADED" && onComplete && !hasCalledCompleteRef.current) {
      completionTimeoutRef.current = setTimeout(() => {
        hasCalledCompleteRef.current = true;
        if (onComplete) {
          onComplete();
        }
      }, 1500);
    }

    // Reset do flag quando status muda para não-LOADED
    if (status !== "LOADED") {
      hasCalledCompleteRef.current = false;
    }

    // Cleanup
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
    };
  }, [status, onComplete]);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress
            variant={isIndeterminate ? "indeterminate" : "determinate"}
            value={isIndeterminate ? undefined : finalPercent}
            sx={{
              height: 12,
              borderRadius: 4,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.secondary.main,
              },
            }}
          />
        </Box>
        {!isIndeterminate && (
          <Box sx={{ minWidth: 50 }}>
            <Typography variant="body2" color="text.secondary">
              {finalPercent}%
            </Typography>
          </Box>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {getStatusMessage()}
      </Typography>
    </Box>
  );
}








